import React from "react";
import { Link } from "react-router-dom";
import { 
  FaChevronUp, FaEnvelope, FaPhoneAlt,
  FaMapMarkerAlt, FaClock
} from "react-icons/fa";

import Paypal from "@/assets/image/PayPal.png";
import MasterCard from "@/assets/image/Mastercard.png";
import Visa from "@/assets/image/Visa.png";
import logo from "@/assets/image/mbbaylogo.png";
import { categories } from "@/components/mockdata/categoryData";

// --- Types ---
type LinkItem = { name: string; path: string };
type PaymentItem = { type: 'image' | 'text'; src?: string; text?: string; alt: string };

// --- Data ---
const customerSupportLinks: LinkItem[] = [
  { name: "FAQ's", path: "/faq" },
  { name: "Contact Support", path: "/contact" },
  { name: "About", path: "/about" },
];

const legalLinks: LinkItem[] = [
  { name: "Customer Terms", path: "/terms" },
  { name: "Vendor Terms", path: "/vendor-terms" },
  { name: "Privacy Policy", path: "/privacy-policy" },
  { name: "Cookie Policy", path: "/cookie-policy" },
  { name: "Returns & cancellations", path: "/return-policy" },
  { name: "Delivery & shipping", path: "/shipping-policy" },
  { name: "Prohibited products", path: "/prohibited-products" },
  { name: "Intellectual property", path: "/intellectual-property" },
  { name: "Vendor fees", path: "/vendor-fees" },
  { name: "Complaints", path: "/complaints" },
];

const sellerLinks: LinkItem[] = [
  { name: "Become a Vendor", path: "/signup-vendor" },
  { name: "Vendor Dashboard", path: "/app" },
];

const accountLinks: LinkItem[] = [
  { name: "Get Started", path: "/selectpath" },
  { name: "Shopping Cart", path: "/cart" },
  { name: "Wishlist", path: "/wishlist" },
  { name: "Products", path: "/random-product" },
];

const contactInfo = [
  { icon: <FaEnvelope />, text: "support@mbaay.com" },
  { icon: <FaPhoneAlt />, text: "+234 707 581 1799 " },
  { icon: <FaMapMarkerAlt />, text: "28A, Oremeta Street, Ojodu Berger, Lagos State, Nigeria" },
  { icon: <FaClock />, text: "Mon - Fri: 9:00 AM - 6:00 PM" },
];

const paymentMethods: PaymentItem[] = [
  { type: 'image', src: Paypal, alt: "PayPal" },
  { type: 'image', src: MasterCard, alt: "MasterCard" },
  { type: 'image', src: Visa, alt: "Visa" },
];

// --- Subcomponents ---
const LinkColumn = ({ title, links }: { title: string; links: LinkItem[] }) => (
  <div>
    <h3 className="font-bold text-white mb-5 text-[15px] tracking-wide">{title}</h3>
    <ul className="space-y-3">
      {links.map((link, index) => (
        <li key={index}>
          <Link 
            to={link.path} 
            className="text-[#a1a1aa] hover:text-[#FF6B00] hover:translate-x-1 inline-block transition-all duration-300 text-[13px] font-semibold focus:outline-none focus:text-[#FF6B00]"
          >
            {link.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

// --- Main Component ---
const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#17171a] text-white pt-16 pb-8 font-sans relative overflow-hidden">
      {/* Back to top button */}
      <div className="absolute right-6 top-6 lg:right-10 lg:top-8 z-10">
        <button 
          onClick={scrollToTop}
          className="w-10 h-10 bg-[#2d2d31] hover:bg-[#FF6B00] text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-lg group focus:outline-none focus:ring-2 focus:ring-[#FF6B00] focus:ring-offset-2 focus:ring-offset-[#17171a]"
          aria-label="Back to top"
        >
          <FaChevronUp className="group-hover:-translate-y-1 transition-transform duration-300" />
        </button>
      </div>

      <div className="px-6 lg:px-10">
        
        {/* Trust Indicators */}
        {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 border-b border-[#2d2d31] pb-10 mb-10">
          {trustIndicators.map((item, idx) => (
            <div key={idx} className="flex items-center gap-4 bg-[#212125] p-4 rounded-xl border border-[#2d2d31] hover:border-[#FF6B00]/50 transition-colors duration-300">
              <div className="text-[#FF6B00] text-3xl shrink-0">
                {item.icon}
              </div>
              <div>
                <h4 className="text-white font-bold text-[14px] leading-tight">{item.title}</h4>
                <p className="text-[#a1a1aa] text-[12px] mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div> */}

        {/* Main Links Area */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 mb-12">
          
          {/* Brand & Contact */}
          <div className="lg:w-[35%] flex flex-col gap-6">
            <div>
              <Link to="/" className="inline-block focus:outline-none focus:ring-2 focus:ring-[#FF6B00] rounded-sm">
                <img src={logo} alt="Mbaay Logo" className="h-20 mb-4 brightness-0 invert" />
              </Link>
              <p className="text-[#a1a1aa] text-[13px] leading-relaxed pr-4 font-medium">
                An online marketplace for buyers and vendors.
              </p>
            </div>
            
            <ul className="space-y-3.5 mt-2">
              {contactInfo.map((info, idx) => (
                <li key={idx} className="flex items-start gap-3 text-[#a1a1aa] text-[13px] font-medium leading-relaxed">
                  <span className="text-[#FF6B00] mt-0.5 text-[15px] shrink-0">{info.icon}</span>
                  <span>{info.text}</span>
                </li>
              ))}
            </ul>

          </div>

          {/* Links Grid */}
          <div className="lg:w-[65%] grid grid-cols-2 md:grid-cols-4 gap-8">
            <LinkColumn title="Customer Support" links={customerSupportLinks} />
            <LinkColumn title="For Sellers" links={sellerLinks} />
            <LinkColumn title="Legal" links={legalLinks} />
            <LinkColumn title="My Account" links={accountLinks} />
          </div>
        </div>

        {/* Categories Area */}
        <div className="border-t border-[#2d2d31] pt-10 mb-10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-white text-[16px]">Popular Categories</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10">
            {categories.slice(0, 4).map((category, idx) => (
              <div key={idx}>
                <h4 className="font-bold text-[#e4e4e7] mb-4 text-[14px]">{category.name}</h4>
                <ul className="space-y-2.5">
                  {category.subcategories.slice(0, 8).map((subcat, linkIdx) => (
                    <li key={linkIdx}>
                      <Link 
                        to={subcat.link} 
                        className="text-[#a1a1aa] hover:text-[#FF6B00] transition-colors text-[13px] font-medium inline-block focus:outline-none focus:text-[#FF6B00]"
                      >
                        {subcat.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-[#2d2d31] pt-10 flex flex-col lg:flex-row justify-between items-start lg:items-end gap-10">
          
          {/* Copyright & Payments */}
          <div className="space-y-6 w-full lg:w-auto order-2 lg:order-1">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-white text-[14px] font-bold">Payment methods shown:</span>
                <div className="flex flex-wrap gap-2">
                  {paymentMethods.map((method, index) => (
                    <div 
                      key={index} 
                      className="bg-white rounded px-2 py-1 flex items-center justify-center min-w-[42px] h-[26px]"
                      aria-label={method.alt}
                      title={method.alt}
                    >
                      {method.type === 'image' && method.src ? (
                        <img src={method.src} alt={method.alt} className="h-[14px] w-auto object-contain" />
                      ) : (
                        <span className="text-[10px] font-bold text-gray-900 tracking-wide uppercase whitespace-nowrap">{method.text}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-[#a1a1aa] text-sm font-medium mb-1">
                  © {new Date().getFullYear()} Mbaay Global Marketplace Ltd. All rights reserved.
                </p>
                <p className="text-[#a1a1aa] text-xs font-medium">
                  Mbaay is an online marketplace connecting independent vendors and customers.
                </p>
              </div>
            </div>
          </div>

          {/* Privacy controls */}
          <div className="w-full lg:w-[400px] order-1 lg:order-2">
            <h3 className="font-bold text-white mb-2 text-[15px]">Privacy controls</h3>
            <p className="text-[#a1a1aa] text-[13px] font-medium mb-4 leading-relaxed lg:pr-8">
              You can review or change optional diagnostic-cookie preferences at any time.
            </p>
            <Link to="/cookie-policy" className="inline-flex min-h-11 items-center rounded-md bg-[#FF6B00] px-4 text-sm font-semibold text-white hover:bg-[#e56000] focus:outline-none focus:ring-2 focus:ring-[#FF6B00] focus:ring-offset-2 focus:ring-offset-[#17171a]">
              Cookie preferences
            </Link>
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;
